import { useEffect, useState } from 'react';

import { useSuspenseXinglet } from '../hooks/use-suspense-xinglet';
import type { XingletLoaderProps } from '../xinglet';

import type { Runtime } from './runtime';
import { useRuntime } from './use-runtime';

const RawXingletLoader: React.FC<{ name: string }> = ({ name, ...rest }) => {
  const { component: Component } = useSuspenseXinglet(name);

  return <Component {...rest} />;
};

const DevWrapper: React.FC<
  React.PropsWithChildren<{
    name: string;
    runtime: Runtime;
  }>
> = ({ children, name: initialName, runtime }) => {
  const [{ name }, setState] = useState<{ name: string }>({
    name: initialName,
  });

  useEffect(() => {
    return runtime.events.on('xinglet-reloaded', (otherName) => {
      if (otherName === name) setState({ name });
    });
  }, [runtime, name]);

  return children;
};

const SSRWrapper: React.FC<
  React.PropsWithChildren<{
    fallback?: React.ReactNode;
    name: string;
    runtime: Runtime;
  }>
> = ({ children, fallback, name, runtime }) => {
  const { manifestMap } = runtime;
  const ssr = manifestMap[name]?.metadata?.ssr === true;
  const [enabled, setEnabled] = useState(ssr);

  useEffect(() => setEnabled(true), []);

  return <>{enabled ? children : fallback}</>;
};

export const InternalXingletLoader: React.FC<XingletLoaderProps> = ({
  fallback,
  name,
  ...rest
}) => {
  const runtime = useRuntime();
  let element = <RawXingletLoader name={name} {...rest} />;

  if (LOCAL_DEVELOPMENT) {
    element = (
      <DevWrapper name={name} runtime={runtime}>
        {element}
      </DevWrapper>
    );
  }

  return (
    <SSRWrapper fallback={fallback} name={name} runtime={runtime}>
      {element}
    </SSRWrapper>
  );
};
